import React, { useState } from 'react';
import { Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';

import EPButton from '@/components/EPButton/EPButton';
import { TestState } from '@/store/InstallationValidation/constants';

import {
  ButtonGroupsContainer,
  HistoryTestDataWrapper,
  MobileButtonContainer,
  TestingButtonGroup,
} from './ButtonGroups.styles';
import { ButtonRenderProps } from './ButtonGroups.utils';
import { HistoryTestDropDown } from '../HistoryTests/HistoryTestDropDown';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';

const ButtonRenderer: React.FC<ButtonRenderProps> = ({
  testState,
  onStartTest,
  onCancelTest,
  onTestAgain,
  onEndTest,
  isMobile,
  onMobileSubmitReport,
  handleSubmitClick,
  handleUpdateLastTestData,
  isStartingTest,
  isEndingTest,
  isCancelTest,
  isSubmitting,
  testDate,
  remainingTimeFormatted,
  numberOfUnresolvedCompressors,
  historyTests,
  testedBy,
  numberOfResolvedCompressors,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleSubmitButtonClick = () => {
    if (
      testState === TestState.NEEDS_REVIEW &&
      (numberOfUnresolvedCompressors > 0 || numberOfResolvedCompressors === 0)
    ) {
      const isValid = handleSubmitClick();
      if (isValid) {
        setIsModalVisible(true);
      }
    } else {
      setIsModalVisible(true);
    }
  };

  const handleModalConfirm = () => {
    setIsModalVisible(false);
    onMobileSubmitReport();
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };
  const renderButtonGroup = () => {
    switch (testState) {
      case TestState.INITIAL:
        return (
          <TestingButtonGroup>
            <EPButton type="primary" onClick={onStartTest} loading={isStartingTest} disabled={isStartingTest}>
              {t('START_TEST')}
            </EPButton>
          </TestingButtonGroup>
        );
      case TestState.RUNNING:
        return (
          <TestingButtonGroup>
            <EPButton
              className="end-test-button"
              type="secondary"
              onClick={onEndTest}
              loading={isEndingTest}
              disabled={isEndingTest}
            >
              {t('END_TEST')}
            </EPButton>
            <EPButton
              type="secondary"
              className="cancel-test-button"
              onClick={onCancelTest}
              loading={isStartingTest}
              disabled={isStartingTest}
            >
              {t('CANCEL_TEST')}
            </EPButton>
            <div className="testing-progress">{t('TESTING_IN_PROGRESS', { time: remainingTimeFormatted })}</div>
          </TestingButtonGroup>
        );
      case TestState.NEEDS_REVIEW:
        return (
          <TestingButtonGroup>
            <EPButton type="primary" onClick={onTestAgain} loading={isStartingTest} disabled={isStartingTest}>
              {t('TEST_AGAIN')}
            </EPButton>
            <EPButton
              type="secondary"
              className="submit-report-button"
              loading={isSubmitting}
              onClick={handleSubmitButtonClick}
            >
              {t('SUBMIT_REPORT')}
            </EPButton>
          </TestingButtonGroup>
        );
      case TestState.TEST_SUCCESS:
        return (
          <TestingButtonGroup>
            <EPButton type="primary" onClick={onTestAgain} loading={isStartingTest} disabled={isStartingTest}>
              {t('TEST_AGAIN')}
            </EPButton>
            <EPButton
              type="secondary"
              className="submit-report-button"
              loading={isSubmitting}
              onClick={handleSubmitButtonClick}
            >
              {t('SUBMIT_REPORT')}
            </EPButton>
          </TestingButtonGroup>
        );
      case TestState.SUBMITTED:
        return (
          <HistoryTestDataWrapper>
            <TestingButtonGroup>
              <EPButton type="primary" onClick={onStartTest} loading={isStartingTest} disabled={isStartingTest}>
                {t('START_TEST')}
              </EPButton>
            </TestingButtonGroup>
            <HistoryTestDropDown
              testDate={testDate}
              historyTests={historyTests}
              testedBy={testedBy}
              handleUpdateLastTestData={handleUpdateLastTestData}
            />
          </HistoryTestDataWrapper>
        );
    }
  };

  const renderMobileButton = () => {
    switch (testState) {
      case TestState.INITIAL:
        return (
          <EPButton type="primary" onClick={onStartTest} loading={isStartingTest} disabled={isStartingTest}>
            {t('START_TEST')}
          </EPButton>
        );

      case TestState.RUNNING:
        return (
          <MobileButtonContainer>
            <EPButton
              className="end-test-button"
              type="secondary"
              onClick={onEndTest}
              loading={isEndingTest}
              disabled={isEndingTest}
            >
              {t('END_TEST')}
            </EPButton>
            <EPButton
              type="secondary"
              className="cancel-test-button"
              onClick={onCancelTest}
              loading={isCancelTest}
              disabled={isCancelTest}
            >
              {t('CANCEL_TEST')}
            </EPButton>
          </MobileButtonContainer>
        );

      case TestState.NEEDS_REVIEW:
        return (
          <MobileButtonContainer>
            <EPButton
              type="primary"
              className="submit-report-button"
              loading={isSubmitting}
              onClick={onMobileSubmitReport}
            >
              {t('SUBMIT_REPORT')}
            </EPButton>
            <EPButton
              type="text"
              className="test-again-button"
              onClick={onTestAgain}
              loading={isStartingTest}
              disabled={isStartingTest}
            >
              {t('TEST_AGAIN')}
            </EPButton>
          </MobileButtonContainer>
        );

      case TestState.TEST_SUCCESS:
        return (
          <MobileButtonContainer>
            <EPButton
              type="primary"
              className="submit-report-button"
              loading={isSubmitting}
              onClick={onMobileSubmitReport}
            >
              {t('SUBMIT_REPORT')}
            </EPButton>
            <EPButton
              type="text"
              className="test-again-button"
              onClick={onTestAgain}
              loading={isStartingTest}
              disabled={isStartingTest}
            >
              {t('TEST_AGAIN')}
            </EPButton>
          </MobileButtonContainer>
        );

      case TestState.SUBMITTED:
        return (
          <EPButton type="primary" onClick={onStartTest} loading={isStartingTest} disabled={isStartingTest}>
            {t('START_TEST')}
          </EPButton>
        );
    }
  };

  return (
    <ButtonGroupsContainer>
      <Skeleton
        active
        title={false}
        paragraph={{ rows: 1, width: 100 }}
        loading={isLoading}
        rootClassName="button-skeleton"
      >
        {isMobile ? renderMobileButton() : renderButtonGroup()}
        <ConfirmationModal isVisible={isModalVisible} onConfirm={handleModalConfirm} onCancel={handleModalCancel} />
      </Skeleton>
    </ButtonGroupsContainer>
  );
};
export default ButtonRenderer;
